/**
 * @fileoverview Connect model choices, comparison evidence, history and spend through a compact, bounded admin workspace. The backend owns validated publication and current configuration; this workspace owns editable selections and explicit compare/restore/save actions. `AiSpendDashboard.js` owns spending presentation; `DocumentAiSettings.js` owns document-edit access choices. `kc-internal-backend/services/ai_control/__init__.py` maps execution/accounting ownership. URL state keeps feature settings and report filters connected without changing admission rules.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../../api'
import { AppButton, AppIcon } from '../shared'
import AppModal from '../shared/AppModal'
import DocumentAiSettings from './DocumentAiSettings'
import AiSpendDashboard from './AiSpendDashboard'
import './AiControlCenter.css'

const SECTIONS = [
    { id: 'spend', label: 'Spend & usage' },
    { id: 'features', label: 'Features' },
    { id: 'models', label: 'Models' },
    { id: 'history', label: 'Change history' },
]
const ICONS = { Assistant:'pi pi-comments', Documents:'pi pi-file', Drafting:'pi pi-pencil', Intake:'pi pi-inbox', Calendar:'pi pi-calendar', 'Client communication':'pi pi-envelope', 'Shared defaults':'pi pi-sliders-h' }


const providerName = id => id === 'openai' ? 'OpenAI' : 'Anthropic'


const date = value => value ? new Date(value).toLocaleString() : 'Not checked yet'


const modelName = value => String(value || '').replace(/^gpt-/, 'GPT ').replace(/^claude-/, 'Claude ').replace(/-/g, ' ')


const errorMessage = error => error.response?.data?.error || 'This request could not be completed. Please try again.'


const selection = config => ({ provider:config.provider, model:config.model, settings:{ ...config.settings }, enabled:config.enabled !== false, allow_override:Boolean(config.allow_override) })


const sameModel = (a,b) => a.provider === b.provider && a.model === b.model && JSON.stringify(a.settings) === JSON.stringify(b.settings)



export default function AiControlCenter() {
    const [params, setParams] = useSearchParams()
    const active = SECTIONS.find(s => s.id === params.get('view'))?.id || 'spend'
    const [data,setData] = useState(null)
    const [error,setError] = useState('')
    const [notice,setNotice] = useState('')
    const [loading,setLoading] = useState(true)
    const [query,setQuery] = useState('')
    const [refreshKey,setRefreshKey] = useState(0)
    const [history,setHistory] = useState([])
    const [restore,setRestore] = useState(null)
    const [busy,setBusy] = useState(false)
    const [syncing,setSyncing] = useState(false)
    const syncStarted = useRef(0)
    const load = useCallback(

    async () => {
        setError('')
        try {
            const result = await api.get('/admin/ai/control')
            setData(result.data)
        } catch(e) { setError(errorMessage(e)) }
        finally { setLoading(false) }
    },[])
    useEffect(() => { load() },[load])
    useEffect(() => {
        if (active === 'history') api.get('/admin/ai/history').then(r => setHistory(r.data.items || [])).catch(e => setError(errorMessage(e)))
    },[active, data])
    useEffect(  () => {
        if (params.get('view') !== 'usage') return
        const next = new URLSearchParams(params)
        next.set('view', 'spend')
        setParams(next, { replace: true })
    },[params,setParams])
    useEffect(() => {
        if (!syncing) return undefined
        const timer = setInterval(async () => {
            await load()
            if (Date.now() - syncStarted.current > 90000) setSyncing(false)
        },3000)
        return () => clearInterval(timer)
    },[syncing,load])
    useEffect(() => {
        if (syncing && data?.providers?.filter(p => p.configured).every(p => p.last_attempt && new Date(p.last_attempt).getTime() >= syncStarted.current - 1000)) {
            setSyncing(false)
            setNotice('Model check finished. Your feature settings are unchanged.')
        }
    },[data,syncing])
    const features = data?.features || []
    const selected = active === 'features' ? features.find(f => f.key === params.get('feature')) : null
    const names = Object.fromEntries(features.map(f => [f.key,f.name]))
    const groups = useMemo(() => {
        const rows = (data?.features || []).filter(f => `${f.name} ${f.group} ${f.config.model}`.toLowerCase().includes(query.toLowerCase()))
        return [...new Set(rows.map(f => f.group))].map(group => ({group,rows:rows.filter(f => f.group === group)}))
    },[data,query])
    const navigate =

    section => {
        const next = new URLSearchParams(params)
        next.set('view', section)
        next.delete('feature')
        next.delete('panel')
        setParams(next)
        setQuery('')
    }
    const openFeature =   key => {
        const next = new URLSearchParams(params)
        next.set('view', 'features')
        next.set('feature', key)
        next.delete('panel')
        setParams(next)
    }
    const closeFeature =   () => {
        const next = new URLSearchParams(params)
        next.delete('feature')
        next.delete('panel')
        setParams(next)
    }
    const refresh =   () => { load(); setRefreshKey(value => value + 1) }
    const refreshModels =

    async () => {
        setError(''); setNotice(''); setSyncing(true); syncStarted.current = Date.now()
        try { await api.post('/admin/ai/models/refresh') }
        catch(e) { setError(errorMessage(e)); setSyncing(false) }
    }
    const restoreSetting =

    async () => {
        setBusy(true); setError('')
        try {
            const feature = features.find(f => f.key === restore.task_key)
            await api.post(`/admin/ai/features/${restore.task_key}/restore/${restore.id}`, {expected_revision:feature?.own_revision ?? null})
            setRestore(null); setNotice('Previous setting restored for new jobs.'); await load()
        } catch(e) { setError(errorMessage(e)) }
        finally { setBusy(false) }
    }
    return <div className="ai-center">
        <header className="ai-center__header">
            <h2>AI</h2>
            <nav className="ai-center__tabs" aria-label="AI sections">{SECTIONS.map(section => <button type="button" key={section.id} aria-current={active===section.id ? 'page' : undefined} className={active===section.id ? 'is-active' : ''} onClick={() => navigate(section.id)}>{section.label}</button>)}</nav>
            <div className="ai-center__header-actions"><span className="ai-center__environment">{data?.environment || 'Loading'}</span><AppButton label="Refresh" size="small" icon="pi pi-refresh" variant="secondary" onClick={refresh} disabled={loading} /></div>
        </header>
        <div key={active} className={`ai-center__content ai-center__content--${active}`}>
        {error && <div className="ai-center__notice is-error" role="alert">{error}</div>}
        {notice && <div className="ai-center__notice" role="status">{notice}</div>}
        {active==='spend' && <AiSpendDashboard features={features} environment={data?.environment} refreshKey={refreshKey} onFeature={openFeature} />}
        {loading && active!=='spend' && <p role="status">Loading AI settings…</p>}
        {!loading && data && <>
            {active==='features' && <>
                {!!data.notices?.length && <details className="ai-center__alerts"><summary><AppIcon icon="pi pi-exclamation-triangle" />{data.notices.length} model notice{data.notices.length === 1 ? '' : 's'}</summary>{data.notices.map(n => <div className={`ai-center__notice ${n.kind==='warning' ? 'is-warning' : ''}`} key={n.id}><AppIcon icon={n.kind==='warning' ? 'pi pi-exclamation-triangle' : 'pi pi-sparkles'} />{n.message}</div>)}</details>}
                <div className="ai-center__toolbar"><label className="ai-center__search"><AppIcon icon="pi pi-search" /><input aria-label="Find an AI feature" placeholder="Find a feature…" value={query} onChange={e => setQuery(e.target.value)} /></label><span>{groups.reduce((total, group) => total + group.rows.length, 0)} of {features.length} features</span></div>
                {groups.map(({group,rows}) => <section className="ai-center__group" key={group}>
                    <h3><AppIcon icon={ICONS[group] || 'pi pi-sparkles'} />{group}<span>{rows.length}</span></h3>
                    {rows.map(f => <div className="ai-center__feature" key={f.key}>
                        <div className="ai-center__feature-name"><strong title={f.name}>{f.name}</strong>{f.inherited && <span className="ai-center__inherited" title={`Uses ${names[f.parent] || 'shared default'}`} aria-label={`Uses ${names[f.parent] || 'shared default'}`}><AppIcon icon="pi pi-link" /></span>}</div>
                        <div className="ai-center__model" title={`${modelName(f.config.model)} · ${providerName(f.config.provider)}`}><span>{modelName(f.config.model)}</span><small>{providerName(f.config.provider)}</small></div>
                        <span className={`ai-center__status ${f.config.enabled ? 'is-on' : 'is-paused'}`}><AppIcon icon={f.config.enabled ? 'pi pi-check-circle' : 'pi pi-pause'} />{f.config.enabled ? 'On' : 'Paused'}</span>
                        <AppButton icon="pi pi-pencil" tooltip={`Manage ${f.name}`} size="small" aria-label={`Manage ${f.name}`} variant="secondary" onClick={() => openFeature(f.key)} />
                    </div>)}
                </section>)}
                {!groups.length && <p className="ai-center__empty">No features match your search.</p>}
            </>}
            {active==='models' && <>
                <div className="ai-center__toolbar"><label className="ai-center__search"><AppIcon icon="pi pi-search" /><input aria-label="Find a model" placeholder="Find a model…" value={query} onChange={e => setQuery(e.target.value)} /></label><AppButton size="small" label={syncing ? 'Checking…' : 'Check for new models'} icon="pi pi-refresh" disabled={syncing} onClick={refreshModels} /></div>
                <div className="ai-center__providers">{data.providers.map(p => <section key={p.id}><h3>{p.name}</h3><span className={`ai-center__status ${p.configured && !p.error ? 'is-on' : 'is-paused'}`}><AppIcon icon={p.configured && !p.error ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'} />{!p.configured ? 'Not connected' : p.error ? 'Needs attention' : 'Connected'}</span><p>Last checked {date(p.last_success)}</p>{p.error && <p>{p.error}</p>}<a href={p.docs} target="_blank" rel="noreferrer">Provider models ↗</a></section>)}</div>
                <div className="ai-center__catalog">{data.models.filter(m => m.candidate && `${m.name} ${m.provider}`.toLowerCase().includes(query.toLowerCase())).map(m => <article key={`${m.provider}:${m.model}`}>
                    <div><strong title={modelName(m.name)}>{modelName(m.name)}</strong><small>{providerName(m.provider)}</small></div>
                    <span className={`ai-center__status ${m.available ? '' : 'is-paused'}`}>{m.used_by.length ? `Used by ${m.used_by.length} tasks` : !m.available ? 'Unavailable' : m.metadata.approved_tasks?.length ? 'Previously approved' : 'Needs example check'}</span>
                    {m.metadata.shutdown_date && <p className="ai-center__retirement">Retires {m.metadata.shutdown_date}</p>}
                    <details><summary>Details and affected features</summary><code>{m.model}</code><p>{/\d{4}-?\d{2}-?\d{2}$/.test(m.model) ? 'Dated model version' : 'Provider alias: its underlying model may change.'}</p><p>{m.used_by.map(key => names[key]).join(', ') || 'Not assigned to any feature.'}</p><p>First seen {date(m.first_seen)}. {m.metadata.pricing ? `Pricing checked ${m.metadata.pricing.checked_at}.` : 'Pricing not yet verified.'}</p></details>
                </article>)}</div>
            </>}
            {active==='history' && <section className="ai-center__history">{history.map(row => <article key={row.id}><div><strong>{names[row.task_key] || row.task_key}</strong><p>{row.previous ? `${modelName(row.previous.model)} → ` : ''}{modelName(row.config.model)} · {row.config.enabled ? 'On' : 'Paused'}</p><small>{date(row.created_at)} · {row.actor}</small><small>{row.note}</small></div><AppButton label="Restore this setting" size="small" variant="secondary" disabled={features.find(f => f.key===row.task_key)?.own_revision===row.id} onClick={() => setRestore(row)} /></article>)}{!history.length && <p className="ai-center__empty">Changes will appear here.</p>}</section>}
        </>}
        </div>
        {selected && <ModelPanel key={selected.key} feature={selected} names={names} initialAccess={params.get('panel')==='access'} onClose={closeFeature} onSaved={async () => {closeFeature();setNotice('Setting applied to new jobs. Existing jobs keep their recorded model.');await load()}} />}
        <AppModal visible={Boolean(restore)} title="Restore this setting?" onClose={() => !busy && setRestore(null)} footer={<AppButton label={busy ? 'Restoring…' : 'Restore setting'} onClick={restoreSetting} disabled={busy} />}>
            {restore && <><p>{names[restore.task_key]} will use <strong>{modelName(restore.config.model)}</strong> for new jobs.</p><p>Status: {restore.config.enabled ? 'On' : 'Paused'}. This creates a new entry in Change history.</p></>}
        </AppModal>
    </div>
}



function ModelPanel({feature,names,initialAccess=false,onClose,onSaved}) {
    const [config,setConfig] = useState(() => selection(feature.config))
    const [comparison,setComparison] = useState(null)
    const [error,setError] = useState('')
    const [busy,setBusy] = useState(false)
    const [access,setAccess] = useState(initialAccess)
    const [modelQuery,setModelQuery] = useState('')
    const [showAll,setShowAll] = useState(false)
    const panelRef = useRef(null)
    const alive = useRef(true)
    useEffect(() => {alive.current=true;return () => {alive.current=false}},[])
    useEffect(

    () => {
        const previous = document.activeElement
        const dialog = panelRef.current?.closest('[role="dialog"]')
        panelRef.current?.querySelector('select')?.focus()
        const trap =

        event => {
            if (event.key !== 'Tab' || !dialog) return
            const elements = [...dialog.querySelectorAll('button:not(:disabled),select:not(:disabled),input:not(:disabled),a[href],summary')].filter(e => e.offsetParent !== null)
            const first=elements[0], last=elements[elements.length-1]
            if (event.shiftKey && document.activeElement===first) {event.preventDefault();last?.focus()}
            else if (!event.shiftKey && document.activeElement===last) {event.preventDefault();first?.focus()}
        }
        dialog?.addEventListener('keydown',trap)
        return () => {dialog?.removeEventListener('keydown',trap);previous?.focus()}
    },[])
    useEffect(() => {
        if (!comparison?.id || ['completed','failed'].includes(comparison.status)) return undefined
        const timer=setInterval(

        async () => {
            try {const r=await api.get(`/admin/ai/comparisons/${comparison.id}`);if(alive.current) setComparison(r.data)}
            catch(e) {if(alive.current){setError(errorMessage(e));setComparison(c => ({...c,status:'failed'}))}}
        },1500)
        return () => clearInterval(timer)
    },[comparison?.id,comparison?.status])
    const update =

    patch => {setConfig(c => ({...c,...patch}));setComparison(null);setError('')}
    const checking = comparison && !['completed','failed'].includes(comparison.status)
    const passed = comparison?.result?.candidate?.passed
    const unchanged = sameModel(config,feature.config)
    const tryExample =

    async () => {
        setBusy(true);setError('')
        try {const r=await api.post(`/admin/ai/features/${feature.key}/compare`,config);if(alive.current) setComparison({...r.data,status:'queued'})}
        catch(e) {if(alive.current) setError(errorMessage(e))}
        finally {if(alive.current) setBusy(false)}
    }
    const apply =

    async () => {
        setBusy(true);setError('')
        try {await api.put(`/admin/ai/features/${feature.key}`,{...config,expected_revision:feature.own_revision ?? null,comparison_id:comparison?.id});await onSaved()}
        catch(e) {if(alive.current) setError(errorMessage(e))}
        finally {if(alive.current) setBusy(false)}
    }
    const allChoices=feature.choices || []
    const rank =

    m => m.model===config.model && m.provider===config.provider ? 3 : m.metadata?.approved_tasks?.includes(feature.key) ? 2 : 1
    const sortedChoices=[...allChoices].sort((a,b) => rank(b)-rank(a) || Number(b.metadata?.created || 0)-Number(a.metadata?.created || 0) || b.model.localeCompare(a.model))
    const choices=modelQuery ? sortedChoices.filter(m => `${m.name} ${m.model} ${m.provider}`.toLowerCase().includes(modelQuery.toLowerCase())) : showAll ? sortedChoices : sortedChoices.slice(0,12)
    const chosen=allChoices.find(m => m.provider===config.provider && m.model===config.model)
    const price=chosen?.metadata?.pricing
    return <AppModal visible title={feature.name} description={`${feature.group} · Changes apply to new jobs`} onClose={() => !busy && onClose()} width={760} className="ai-model-panel" footer={!access && <><AppButton label={checking ? 'Checking example…' : 'Try on an example'} variant="secondary" onClick={tryExample} disabled={busy || checking} /><AppButton label={busy ? 'Working…' : 'Apply to this feature'} onClick={apply} disabled={busy || checking || (!unchanged && !passed)} /></>}>
        <div ref={panelRef}>
            {feature.key==='document-ai-edits' && <div className="ai-center__tabs"><button className={!access ? 'is-active' : ''} onClick={() => setAccess(false)}>Model</button><button className={access ? 'is-active' : ''} onClick={() => setAccess(true)}>Access</button></div>}
            {access ? <DocumentAiSettings accessOnly /> : <>
                {error && <p className="ai-center__notice is-error" role="alert">{error}</p>}
                <div className="ai-model-panel__current"><span>Current</span><strong>{modelName(feature.config.model)}</strong><small>{providerName(feature.config.provider)}</small></div>
                <label className="ai-center__search"><input aria-label="Search available models" placeholder="Search available models…" value={modelQuery} onChange={e => setModelQuery(e.target.value)} /></label>
                <label className="ai-model-panel__field">Model<select value={`${config.provider}:${config.model}`} onChange={e => {const chosen=allChoices.find(m => `${m.provider}:${m.model}`===e.target.value);if(chosen) update({provider:chosen.provider,model:chosen.model,settings:chosen.provider===config.provider ? config.settings : {}})}}>
                    {!choices.some(m => m.provider===config.provider && m.model===config.model) && <option value={`${config.provider}:${config.model}`}>{modelName(config.model)} · Current</option>}
                    {choices.map(m => <option key={`${m.provider}:${m.model}`} value={`${m.provider}:${m.model}`} disabled={Boolean(m.disabled_reason)}>{modelName(m.name)} · {providerName(m.provider)}{m.model===feature.config.model && m.provider===feature.config.provider ? ' · Current' : ''}{m.disabled_reason ? ` · ${m.disabled_reason}` : ''}</option>)}
                </select></label>
                {!modelQuery && allChoices.length>12 && <button type="button" className="ai-center__text-button" onClick={() => setShowAll(v=>!v)}>{showAll ? 'Show current and recent choices' : `Show all ${allChoices.length} models`}</button>}
                {price && <p className="ai-center__muted">${price.input_per_million} input · ${price.output_per_million} output per million tokens. <a href={price.source} target="_blank" rel="noreferrer">Pricing ↗</a></p>}
                <label className="ai-model-panel__check"><input type="checkbox" checked={config.enabled} onChange={e => update({enabled:e.target.checked})} />Allow new AI requests</label>
                {feature.key.startsWith('intake-') || feature.key.startsWith('drafting-') ? <label className="ai-model-panel__check"><input type="checkbox" checked={config.allow_override} onChange={e => update({allow_override:e.target.checked})} />Allow a different approved model for an individual run</label> : null}
                <details className="ai-model-panel__advanced"><summary>Advanced settings</summary>
                    {config.provider==='openai' && <label className="ai-model-panel__field">Reasoning<select value={config.settings.reasoning_effort ?? ''} onChange={e => update({settings:{...config.settings,reasoning_effort:e.target.value || null}})}>{[['','Provider default'],['none','None — fastest'],['minimal','Minimal'],['low','Low'],['medium','Medium'],['high','High'],['xhigh','Extra high'],['max','Maximum']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></label>}
                    <label className="ai-model-panel__field">Maximum response tokens<input type="number" min="64" max="30000" placeholder="Feature default" value={config.settings.max_output_tokens || ''} onChange={e => {const settings={...config.settings};if(e.target.value) settings.max_output_tokens=Number(e.target.value);else delete settings.max_output_tokens;update({settings})}} /></label>
                    <code>{config.model}</code>
                </details>
                {!!feature.affected?.length && <div className="ai-center__notice">Also changes: {feature.affected.map(key => names[key] || key).join(', ')}.</div>}
                <p className="ai-center__muted">The example uses fictional facts and makes provider requests. It never sends messages or changes documents.</p>
                {comparison && <div aria-live="polite">{checking ? <p role="status">Comparing the current model and your selection…</p> : comparison.result?.candidate ? <>
                    <div className="ai-model-panel__results">{['current','candidate'].map(side => {const r=comparison.result[side];return <section key={side}><h4>{side==='current' ? 'Current model' : 'Your selection'}</h4><span className={`ai-center__status ${r.passed ? 'is-on' : 'is-paused'}`}>{r.passed ? 'Example passed' : 'Needs attention'}</span><p>{r.output || r.error}</p><small>{(r.latency_ms/1000).toFixed(1)} seconds</small></section>})}</div><p className="ai-center__muted">{comparison.result.note}</p>
                </> : <p role="alert">{comparison.result?.error || 'Comparison failed. Please try again.'}</p>}</div>}
            </>}
        </div>
    </AppModal>
}
